import React, { useState } from 'react';
import './RightTabs.css';
import Loader from '../Loader/Loader';
import { useTranslation } from 'react-i18next';

const RightTabs = ({ allReferences, search_strategy, extReferences, onTabChange, activeTab: externalActiveTab }) => {
    const { t } = useTranslation();

    const downloadBibTeX = (bibTeXContent, refNumber = 'all') => {
        const blob = new Blob([bibTeXContent], { type: 'text/plain' });

        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);

        // Dinamik dosya adı
        const fileName = `piriai_reference_${refNumber}.bib`;
        link.download = fileName;

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(link.href);
    };


    const [localActiveTab, setLocalActiveTab] = useState('references');
    const activeTab = externalActiveTab || localActiveTab;
    const [expandedIndex, setExpandedIndex] = useState(null);

    const handleTabChange = (tab) => {
        setLocalActiveTab(tab);
        if (onTabChange) {
            onTabChange(tab);
        }
    };

    const formatStrategy = (strategyString) => {
        if (!strategyString) return null;
        try {
            const cleanedString = strategyString.replace(/[\[\]]/g, '').trim();
            const strategies = cleanedString.split(',').map(s => s.trim());


            return (<div className="strategy-list">
                {strategies.map((strategy, index) => (<div key={index} className="strategy-item flex items-center mb-2">
                    <span className="strategy-number mr-2">{index + 1}.</span>
                    <a
                        href={`https://kesifaraci.com/index.jsp?modul=konu_kesfet_detay&search_type=keyword&searchword=${encodeURIComponent(strategy)}`}
                        target="_blank" // Yeni sekmede açar
                        rel="noopener noreferrer" // Güvenlik için rel eklenir
                        className="strategy-text text-blue-500 hover:underline"
                    >
                        {strategy}
                    </a>
                </div>))}
            </div>);

        } catch (e) {
            console.error("Error parsing strategy:", e);
            return <p>{strategyString}</p>;
        }
    };

    const formatAuthors = (authors) => {
        if (!authors) return '';
        const authorList = authors
            .split(',')
            .map(author => author.trim())
            .filter(author => author); // Boş elemanları temizler

        if (authorList.length <= 5) {
            return authorList.join(', '); // Gereksiz virgül ve boşluklar giderilmiş olur
        }

        return authorList.slice(0, 5).join(', ') + ' ...';
    };

    const renderReferenceField = (label, value) => {
        if (!value || value === '0' || value === 0) return null;
        return <p><strong>{label}:</strong> {value}</p>;
    };

    const formatReferences = (refsString) => {

        const citationFormats = {
            APA: (ref) => {
                const volumeIssue = ref.volume ? ref.volume : '';
                const issue = ref.issue ? `(${ref.issue})` : '';
                const pages = (ref.first_page && ref.last_page) ? `, ${ref.first_page}-${ref.last_page}` : '';
                const cited = (ref.citedByCount !== null && ref.citedByCount !== undefined) ? ` Cited by: ${ref.citedByCount}` : '';
                return `${ref.authors}. (${ref.publicationYear}). ${ref.title}. ${ref.primaryLocationDisplayName}${volumeIssue || issue ? ', ' : ''}${volumeIssue}${issue}${pages}.${cited}${ref.doi && ref.doi !== 'null' ? ' DOI: ' + ref.doi : ''}`;
            },

            Harvard: (ref) => {
                const volumeIssue = ref.volume ? ref.volume : '';
                const issue = ref.issue ? `(${ref.issue})` : '';
                const pages = (ref.first_page && ref.last_page) ? `, pp. ${ref.first_page}-${ref.last_page}` : '';
                return `${ref.authors} (${ref.publicationYear}) '${ref.title}', ${ref.primaryLocationDisplayName}${volumeIssue || issue ? ', ' : ''}${volumeIssue}${issue}${pages}${ref.doi && ref.doi !== 'null' ? ', DOI: ' + ref.doi : ''}`;
            },

            MLA: (ref) => {
                const volume = ref.volume ? ref.volume : '';
                const issue = ref.issue ? ref.issue : '';
                const year = ref.publicationYear ? ref.publicationYear : '';
                const pages = (ref.first_page && ref.last_page) ? `${ref.first_page}-${ref.last_page}` : '';
                let volIssue = '';
                if (volume && issue) volIssue = `${volume}, no. ${issue}`; else if (volume) volIssue = volume; else if (issue) volIssue = `no. ${issue}`;
                return `${ref.authors}. "${ref.title}." ${ref.primaryLocationDisplayName}, ${volIssue ? volIssue + ', ' : ''}${year}${pages ? ', pp. ' + pages : ''}${ref.doi && ref.doi !== 'null' ? '. DOI: ' + ref.doi : ''}.`;
            },

            Chicago: (ref) => {
                const volume = ref.volume ? ref.volume : '';
                const issue = ref.issue ? ref.issue : '';
                const year = ref.publicationYear ? ref.publicationYear : '';
                const pages = (ref.first_page && ref.last_page) ? `${ref.first_page}-${ref.last_page}` : '';
                let volIssue = '';
                if (volume && issue) volIssue = `${volume}, no. ${issue}`; else if (volume) volIssue = volume; else if (issue) volIssue = `no. ${issue}`;
                return `${ref.authors}. "${ref.title}." ${ref.primaryLocationDisplayName}${volIssue ? ' ' + volIssue : ''} (${year})${pages ? ': ' + pages : ''}${ref.doi && ref.doi !== 'null' ? '. DOI: ' + ref.doi : '.'}`;
            },

            IEEE: (ref) => {
                const volume = ref.volume ? ref.volume : '';
                const issue = ref.issue ? ref.issue : '';
                const pages = (ref.first_page && ref.last_page) ? `${ref.first_page}-${ref.last_page}` : '';
                let volIssue = '';
                if (volume && issue) volIssue = `${volume}, no. ${issue}`; else if (volume) volIssue = volume; else if (issue) volIssue = `no. ${issue}`;
                return `[${ref.refNumber}] ${ref.authors}, "${ref.title}," ${ref.primaryLocationDisplayName}${volIssue ? ', vol. ' + volIssue : ''}${pages ? ', pp. ' + pages : ''}, ${ref.publicationYear}${ref.doi && ref.doi !== 'null' ? '. DOI: ' + ref.doi : '.'}`;
            },

            BibTeX: (ref) => {
                // BibTeX anahtarında sadece ilk yazarın soyadının ilk kelimesini, yıl ile birleştiriyoruz.
                // Yazarlar stringinde virgül varsa ilk yazarın soyadı ilk kelime olarak alınır.
                let firstAuthor = '';
                if (ref.authors.includes(',')) {
                    firstAuthor = ref.authors.split(',')[0].split(' ').slice(-1)[0];
                } else {
                    firstAuthor = ref.authors.split(' ')[0];
                }
                const key = firstAuthor.toLowerCase() + ref.publicationYear;

                // pages
                let pages = '';
                if (ref.first_page && ref.last_page) pages = `${ref.first_page}--${ref.last_page}`;

                // volume, number(issue)
                let volumeNumber = '';
                if (ref.volume) {
                    volumeNumber = `volume = {${ref.volume}},\n`;
                }
                if (ref.issue) {
                    volumeNumber += `number = {${ref.issue}},\n`;
                }

                return `@article{${key},
  author = {${ref.authors}},
  title = {${ref.title}},
  journal = {${ref.primaryLocationDisplayName}},
  year = {${ref.publicationYear}},
  ${volumeNumber}${pages ? `pages = {${pages}},\n` : ''}${ref.doi && ref.doi !== 'null' ? `doi = {${ref.doi}}` : ''}
`;


            }
        };

        const refs = JSON.parse(refsString);

        const generateBibTeXContent = () => {
            return refs.map((ref) => {
                // BibTeX anahtarı oluşturma: ilk yazar soyadı + yıl
                let firstAuthor = '';
                if (ref.authors) {
                    if (ref.authors.includes(',')) {
                        // Virgülle ayrılmış yazar listesinde ilk yazarın soyadını al
                        firstAuthor = ref.authors.split(',')[0].trim().split(' ').slice(-1)[0];
                    } else {
                        // Tek yazar veya boşluklu isimde ilk kelime
                        firstAuthor = ref.authors.trim().split(' ')[0];
                    }
                }
                const key = firstAuthor.toLowerCase() + (ref.publicationYear || '');

                // Sayfa aralığı
                const pages = (ref.first_page && ref.last_page) ? `${ref.first_page}--${ref.last_page}` : '';

                // volume ve issue (number)
                let volumeNumber = '';
                if (ref.volume) {
                    volumeNumber += `  volume = {${ref.volume}},\n`;
                }
                if (ref.issue) {
                    volumeNumber += `  number = {${ref.issue}},\n`;
                }

                return `@article{${key},
  author = {${ref.authors || ''}},
  title = {${ref.title || ''}},
  journal = {${ref.primaryLocationDisplayName || ''}},
  year = {${ref.publicationYear || ''}},
${volumeNumber}${pages ? `  pages = {${pages}},\n` : ''}${ref.doi && ref.doi !== 'null' ? `  doi = {${ref.doi}}` : ''}`
                    .trim();



            }).join('\n\n');
        };


        if (!refsString) return null;
        try {
            const refs = JSON.parse(refsString);
            return (<div className="references-list">
                <button
                    onClick={() => downloadBibTeX(generateBibTeXContent())}
                    className="inline-flex items-center justify-center w-full text-sm bg-sky-50 text-sky-700 px-3 py-1.5 rounded-md border border-sky-200 hover:bg-sky-100 hover:border-sky-300 transition-all duration-200 mb-3 hover:shadow-sm"
                >
                    <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
                            d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                    </svg>
                    {t('Tümünü BibTeX Olarak İndir')}
                </button>

                {refs.map((ref, index) => (<div
                    key={index}
                    className="reference-item"
                    data-reference-id={ref.refNumber}
                >
                    <h3>[{ref.refNumber}] {ref.title}</h3>
                    {renderReferenceField("Authors", formatAuthors(ref.authors))}
                    {renderReferenceField("Year", ref.publicationYear)}
                    {renderReferenceField("Journal", ref.primaryLocationDisplayName)}
                    {renderReferenceField("Summary", ref.summary)}
                    <div className="article-link-container">
                        {/* Bilgi Mesajı: Eksik Veriler */}
                        {ref.doi === null && (<div className="info-message">
                            {t("PDF'de DOI bilgisi eksik.")}
                        </div>)}

                        {!ref.primaryLocationDisplayName && (<div className="info-message">
                            {t("PDF'de dergi adı eksik.")}
                        </div>)}

                        {/* Makaleye Git (Eğer DOI varsa) */}
                        {ref.doi && (<a
                            href={`${ref.doi}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="article-link"
                        >
                            {t('Makaleye Git')}
                        </a>)}

                        {/* Alıntı Yap */}
                        <button
                            onClick={() => setExpandedIndex(expandedIndex === index ? null : index)}
                            className="article-link"
                        >
                            {t('Alıntı Yap')}
                        </button>

                        {/* BibTeX İndir */}
                        <button
                            className="article-link"
                            onClick={() => downloadBibTeX(citationFormats.BibTeX(ref), ref.refNumber)}
                        >
                            {t('BibTeX İndir')}
                        </button>
                    </div>


                    {/* Açılır Bölüm */}
                    {expandedIndex === index && (
                        <div className="citation-section mt-4 p-4 border border-gray-200 rounded bg-gray-50">
                            <h4 className="font-bold mb-2">{t('Atıf Formatları')}:</h4>
                            <ul className="list-disc pl-5 text-left">
                                {Object.keys(citationFormats).map((format) => (<li key={format}>
                                    <strong>{format}:</strong> {citationFormats[format](ref)}
                                </li>))}
                            </ul>
                        </div>

                    )}
                </div>))}
            </div>);
        } catch (e) {
            console.error("Error parsing references:", e);
            return <p> {t('Referanslar yüklenirken bir hata oluştu.')}</p>;
        }
    };

    return (<div className="right-section">
        <div className="right-tabs">
            <button
                className={`right-tab ${activeTab === 'references' ? 'active' : ''}`}
                onClick={() => handleTabChange('references')}
            >
                {t('Referanslar')}
            </button>
            <button
                className={`right-tab ${activeTab === 'strategy' ? 'active' : ''}`}
                onClick={() => handleTabChange('strategy')}
            >
                {t('Terimler')}
            </button>
            <button
                className={`right-tab ${activeTab === 'similar' ? 'active' : ''}`}
                onClick={() => handleTabChange('similar')}
            >
                {t('Benzer Eserler')}
            </button>
        </div>

        <div className="right-tab-content">
            {activeTab === 'references' && (<div className="scrollable-content">
                {allReferences === null || allReferences === undefined ? (
                    <Loader />) : (formatReferences(allReferences))}
            </div>)}
            {activeTab === 'strategy' && (<div className="scrollable-content">
                {search_strategy === null || search_strategy === undefined ? (
                    <Loader />) : (formatStrategy(search_strategy))}
            </div>)}
            {activeTab === 'similar' && (<div className="scrollable-content">
                {extReferences === null || extReferences === undefined ? (
                    <Loader />) : (formatReferences(extReferences))}
            </div>)}
        </div>
    </div>);
};

export default RightTabs;